import React, { useState, useRef, useEffect, Suspense } from "react";
import { useTranslation } from "react-i18next";
import "./CatalogSection.css";

const CatalogViewer = React.lazy(() => import("./CatalogViewer"));

const EyeIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const CatalogSection = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const sectionRef = useRef(null);

  // Animate section in when it enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (viewerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [viewerOpen]);

  return (
    <>
      <section
        ref={sectionRef}
        className={`catalog-section${isVisible ? " catalog-visible" : ""}`}
      >
        <div className="container">
          <div className="row align-items-center">
            {/* ── 3D Book visual ── */}
            <div className="col-lg-5 d-flex justify-content-center">
              <div
                className="catalog-book-wrapper"
                onClick={() => setViewerOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setViewerOpen(true)}
                aria-label={t("catalog.ariaOpen")}
              >
                <div className="catalog-book">
                  {/* Spine */}
                  <div className="catalog-book-spine" />
                  {/* Pages stack */}
                  <div className="catalog-book-pages" />
                  {/* Front cover */}
                  <div className="catalog-book-front">
                    <div className="catalog-book-cover">
                      {/* Diamond ornament */}
                      <svg
                        className="catalog-cover-ornament"
                        viewBox="0 0 40 40"
                        fill="none"
                      >
                        <rect
                          x="20"
                          y="2"
                          width="25"
                          height="25"
                          rx="1"
                          transform="rotate(45 20 20)"
                          stroke="rgba(221,209,190,0.45)"
                          strokeWidth="1"
                        />
                        <rect
                          x="20"
                          y="7"
                          width="18"
                          height="18"
                          rx="1"
                          transform="rotate(45 20 20)"
                          stroke="rgba(221,209,190,0.25)"
                          strokeWidth="0.5"
                        />
                        <circle
                          cx="20"
                          cy="20"
                          r="2"
                          fill="rgba(221,209,190,0.4)"
                        />
                      </svg>

                      <div className="catalog-cover-logo">MÜKEMEL</div>
                      <div className="catalog-cover-sub">
                        {t("catalog.coverSub")}
                      </div>
                      <div className="catalog-cover-line" />
                      <div className="catalog-cover-year">2026</div>
                      <div className="catalog-cover-tagline">
                        {t("catalog.coverTagline")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Text content ── */}
            <div className="col-lg-7 catalog-content">
              <div className="catalog-eyebrow">
                <div className="catalog-eyebrow-line" />
                <span className="catalog-eyebrow-text">
                  {t("catalog.eyebrow")}
                </span>
              </div>

              <h2 className="catalog-title">
                {t("catalog.title")}
                <br />
                <em>{t("catalog.titleEm")}</em>
              </h2>

              <p className="catalog-desc">{t("catalog.desc")}</p>

              <div className="catalog-actions">
                <button
                  className="catalog-btn-primary"
                  onClick={() => setViewerOpen(true)}
                >
                  <EyeIcon />
                  {t("catalog.viewBtn")}
                </button>
                <a
                  className="catalog-btn-secondary"
                  href="/dijital.pdf"
                  download={t("catalog.downloadFilename")}
                >
                  <DownloadIcon />
                  {t("catalog.downloadBtn")}
                </a>
              </div>

              <div className="catalog-stats">
                <div className="catalog-stat">
                  <span className="catalog-stat-value">2026</span>
                  <span className="catalog-stat-label">
                    {t("catalog.statCollection")}
                  </span>
                </div>
                <div className="catalog-stat">
                  <span className="catalog-stat-value">PDF</span>
                  <span className="catalog-stat-label">
                    {t("catalog.statFormat")}
                  </span>
                </div>
                <div className="catalog-stat">
                  <span className="catalog-stat-value">13 MB</span>
                  <span className="catalog-stat-label">
                    {t("catalog.statSize")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lazy-loaded viewer — only mounts when user opens it */}
      {viewerOpen && (
        <Suspense
          fallback={
            <div className="catalog-overlay">
              <div className="catalog-loading-state">
                <div className="catalog-spinner-ring" />
                <p
                  style={{
                    color: "rgba(221,209,190,0.6)",
                    letterSpacing: 3,
                    fontSize: "0.75rem",
                  }}
                >
                  {t("catalog.loadingShort")}
                </p>
              </div>
            </div>
          }
        >
          <CatalogViewer onClose={() => setViewerOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default CatalogSection;
